'use client'
import React, { useImperativeHandle, useRef } from 'react'
import { Heading, Picture, Text } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import type { FeatureCardsProps, FeatureCardItem } from './types.js'
import { Swiper, SwiperSlide } from 'swiper/react'

const componentType = 'image'
const componentName = 'feature_cards'

/**
 * 根据卡片数量获取桌面端（laptop+）的网格布局类名
 * - 2张卡片: 2:3
 * - 3张卡片: 1:1:1
 * - 4张卡片: 第一行 2:3，第二行 3:2
 * - 5张卡片: 第一行 1:1:1，第二行 2:3
 */
const getCardLayoutClasses = (index: number, totalCards: number): string => {
  if (totalCards === 2) {
    // 2:3 比例
    return index === 0 ? 'laptop:flex-[2]' : 'laptop:flex-[3]'
  }

  if (totalCards === 3) {
    // 1:1:1 比例
    return 'laptop:flex-[1]'
  }

  if (totalCards === 4) {
    // 第一行 2:3，第二行 3:2
    if (index === 0) return 'laptop:flex-[2]'
    if (index === 1) return 'laptop:flex-[3]'
    if (index === 2) return 'laptop:flex-[3]'
    return 'laptop:flex-[2]'
  }

  if (totalCards === 5) {
    // 第一行 1:1:1，第二行 2:3
    if (index < 3) return 'laptop:flex-[1]'
    if (index === 3) return 'laptop:flex-[2]'
    return 'laptop:flex-[3]'
  }

  // 默认等宽
  return 'laptop:flex-[1]'
}

/**
 * 根据卡片数量确定是否需要换行
 * - 4张卡片: 第一行2张，第二行2张
 * - 5张卡片: 第一行3张，第二行2张
 */
const shouldBreakRow = (index: number, totalCards: number): boolean => {
  if (totalCards === 4 && index === 1) return true
  if (totalCards === 5 && index === 2) return true
  return false
}

const FeatureCards = React.forwardRef<HTMLDivElement, FeatureCardsProps>(({ data, className }, ref) => {
  const { title, subtitle, items = [], theme = 'light' } = data

  const boxRef = useRef<HTMLDivElement>(null)

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: title,
    componentDescription: subtitle,
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  // 统一使用 rounded-box
  const roundedClasses = 'rounded-box'

  const renderCard = (item: FeatureCardItem, index?: number) => {
    const cardContent = (
      <>
        <Picture
          source={item.image?.url}
          alt={item.image?.alt || item.title}
          className="feature-cards__card-image absolute inset-0 !h-full"
          imgClassName="!h-full object-cover"
        />
        <div className="feature-cards__card-content z-10">
          <Heading
            size={3}
            as="h2"
            className="feature-cards__card-title laptop:text-[20px] desktop:text-[32px] lg-desktop:text-[32px]"
          >
            {item.title}
          </Heading>
          <Text
            size={4}
            as="p"
            className="feature-cards__card-subtitle laptop:h-[42px] lg-desktop:h-[54px] lg-desktop:text-[18px] mt-[4px] line-clamp-2 h-[42px] text-[14px]"
          >
            {item.subtitle}
          </Text>
        </div>
      </>
    )

    // 基础样式（移除原有的 rounded 类）
    const baseClasses =
      'feature-cards__card relative flex h-[360px] max-h-[560px] flex-col justify-end gap-[16px] overflow-hidden px-[16px] py-[12px] text-info-primary tablet:h-[336px] laptop:h-[336px] laptop:px-[16px] laptop:py-[16px] desktop:h-[448px] desktop:px-[32px] desktop:py-[24px] lg-desktop:h-[560px]'

    // 获取响应式布局类名（仅用于桌面端）
    const layoutClasses = index !== undefined ? getCardLayoutClasses(index, items.length) : 'flex-1'

    const cardClasses = cn(baseClasses, roundedClasses, layoutClasses, {
      'aiui-dark': item.theme === 'dark',
      'aiui-light': item.theme === 'light',
    })

    if (item.link) {
      return (
        <a
          href={trackUrlRef(item.link, `${componentType}_${componentName}`)}
          className={cardClasses}
          data-headless-type-name={`${componentType}#${componentName}`}
          data-headless-title-desc-button={`${title}#${item.title}#${item.subtitle}`}
        >
          {cardContent}
        </a>
      )
    }

    return <div className={cardClasses}>{cardContent}</div>
  }

  return (
    <section
      ref={boxRef}
      data-ui-component-id="FeatureCards"
      className={cn(
        'feature-cards text-info-primary flex w-full flex-col justify-center',
        {
          'aiui-dark': theme === 'dark',
        },
        className
      )}
    >
      {/* 标题区域 */}
      <div className="feature-cards__header">
        <Heading size={4} as="h1" className="feature-cards__title laptop:text-center text-left">
          {title}
        </Heading>
        {subtitle && (
          <Text
            size={2}
            as="p"
            className="feature-cards__subtitle laptop:text-center desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] text-left text-[14px]"
          >
            {subtitle}
          </Text>
        )}
      </div>

      {/* 桌面端网格布局 */}
      <div className="feature-cards__grid laptop:mt-[32px] laptop:flex desktop:mt-[40px] lg-desktop:mt-[24px] mt-[24px] hidden w-full flex-wrap gap-[16px]">
        {items.map((item, index) => (
          <React.Fragment key={item.title}>
            {renderCard(item, index)}
            {/* 在特定位置插入换行 */}
            {shouldBreakRow(index, items.length) && <div className="laptop:basis-full laptop:h-0" />}
          </React.Fragment>
        ))}
      </div>

      {/* 移动端 Swiper 布局 */}
      <div className="feature-cards__swiper-container laptop:hidden mt-[24px] w-full">
        <Swiper
          spaceBetween={12}
          slidesPerView="auto"
          grabCursor
          className="feature-cards__swiper relative w-full !overflow-visible"
        >
          {items.map(item => (
            <SwiperSlide key={item.title} className="feature-cards__swiper-slide relative !h-[360px] !w-[296px]">
              <div
                key={item.title}
                className={cn(
                  'feature-cards__card feature-cards__card--mobile text-info-primary absolute inset-0 flex flex-1 flex-col justify-end gap-[16px] overflow-hidden px-[16px] py-[12px]',
                  roundedClasses,
                  {
                    'aiui-dark': item.theme === 'dark',
                    'aiui-light': item.theme === 'light',
                  }
                )}
              >
                <Picture
                  source={item.image?.url}
                  alt={item.image?.alt || item.title}
                  className="feature-cards__card-image absolute inset-0 !h-full"
                  imgClassName="!h-full object-cover"
                />
                <div className="feature-cards__card-content z-10">
                  <Heading size={3} as="h2" className="feature-cards__card-title">
                    {item.title}
                  </Heading>
                  <Text
                    size={4}
                    as="p"
                    className="feature-cards__card-subtitle laptop:h-[42px] lg-desktop:h-[54px] lg-desktop:text-[18px] mt-[4px] line-clamp-2 h-[42px] overflow-visible text-[14px]"
                  >
                    {item.subtitle}
                  </Text>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
})

FeatureCards.displayName = 'FeatureCards'

export default withLayout(FeatureCards, { style: 'overflow: hidden;' })
